import { AxiosMethods } from '@setvi/shared/enums';
import { Product } from '@setvi/shared/interfaces';
import { axiosHelper } from '@setvi/shared/services';
import { useMutation } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

enum Endpoints {
  Price = '/pricing/get',
  PriceGroup = '/pricing-groups/pricing'
}

export enum PriceType {
  Price = 'Price',
  PriceGroup = 'PriceGroup'
}

interface MutationProps {
  type?: PriceType;
  body: {
    accountNumber: string;
    products: {
      sku: string;
      quantity: number;
    }[];
  };
}

export interface GetPricesProps {
  type?: PriceType;
  productList: Product[];
  accountNumber: string;
}

export const getProductsPrices = () => ({
  mutationFn: ({ type, body }: MutationProps) =>
    axiosHelper({
      endpoint: Endpoints[(type || PriceType.Price) as keyof typeof Endpoints],
      method: AxiosMethods.POST,
      body,
      baseURL: process.env.REVOPS_API
    })
});

export const useProductsPrices = () => {
  const { mutationFn } = getProductsPrices();
  const mutation = useMutation(mutationFn);

  const getPrices = async ({
    type = PriceType.Price,
    productList,
    accountNumber
  }: GetPricesProps) => {
    try {
      const products = productList.map(product => ({
        sku: product.sku,
        quantity: product.quantity || 1
      }));

      const response: { pricing: Product[] } = await mutation.mutateAsync({
        type,
        body: {
          accountNumber,
          products
        }
      });

      return response?.pricing?.map(product => ({
        ...product,
        price: Number(product.sell)
      }));
    } catch (error) {
      enqueueSnackbar('Error occured while fetching products price', {
        variant: 'error'
      });
      return null;
    }
  };

  return { getPrices, isLoading: mutation.isLoading };
};
